import { sendAjaxRequest } from "../ajax/ajax"
import { AjaxResponse } from "../ajax/types";
import { EndpointRequest, EndpointResponse, EndpointResponseFactory } from "../endpoint"
import { JSONResponse } from "./jsonResponse"

const DefaultResponseFactory: EndpointResponseFactory =
    (response: AjaxResponse) =>
        new JSONResponse(response);

interface ODataParameters {
    pagination?: Pagination;
    filter?: string;
    orderby?: string;
}
const NoParameters: ODataParameters = {};

interface Pagination {
    pageNumber: number;
    pageSize: number
}

function create(
    uri: string,
    request: EndpointRequest,
    responseFactory: EndpointResponseFactory = DefaultResponseFactory): Promise<EndpointResponse> {
    return sendRequest("POST", uri, responseFactory, request);
}

function retrieve(
    uri: string,
    request: EndpointRequest = null,
    responseFactory: EndpointResponseFactory = DefaultResponseFactory): Promise<EndpointResponse> {
    return sendRequest("GET", uri, responseFactory, request);
}

function retrieveCollection(
    uri: string,
    parameters: ODataParameters = null,
    request: EndpointRequest = null,
    responseFactory: EndpointResponseFactory = DefaultResponseFactory): Promise<EndpointResponse> {
    return sendRequest("GET", uri, responseFactory, request, parameters);
}

function update(
    uri: string,
    request: EndpointRequest,
    responseFactory: EndpointResponseFactory = DefaultResponseFactory): Promise<EndpointResponse> {
    return sendRequest("PUT", uri, responseFactory, request);
}

function delete$(
    uri: string,
    request: EndpointRequest = null,
    responseFactory: EndpointResponseFactory = DefaultResponseFactory): Promise<EndpointResponse> {
    return sendRequest("DELETE", uri, responseFactory, request);
}

function sendRequest(
    method: string,
    uri: string,
    responseFactory: EndpointResponseFactory,
    endpointRequest: EndpointRequest = null,
    odataParameters: ODataParameters = null): Promise<EndpointResponse> {
    let headers = endpointRequest == null ?
        {} :
        endpointRequest.headers;

    let body = endpointRequest == null ?
        "" :
        JSON.stringify(endpointRequest.body);

    const uriParts = uri.split("?");
    const uriOdataParameters = queryParametersFromODataParameters(odataParameters);
    if (uriOdataParameters != null) {
        uriParts.push(uriOdataParameters);
    }

    uri = uriParts.reduce((previousValue, currentValue, currentIndex) => {
        return previousValue + (currentIndex == 1 ? "?" : currentIndex > 1 ? "&" : "") + currentValue;
    }, "");

    return sendAjaxRequest(method, uri, body, headers)
        .then(
            response => Promise.resolve(
                responseFactory(response)),
            error => Promise.reject(
                responseFactory(error)));
}

function queryParametersFromODataParameters(odataParameters: ODataParameters = null): string {
    if (odataParameters == null) {
        return null;
    }

    const queryParameters: { [key: string]: string } = {};

    if (odataParameters.pagination != null) {
        const pageNumber = odataParameters.pagination.pageNumber;
        const pageSize = odataParameters.pagination.pageSize;

        queryParameters["$count"] = "true";
        queryParameters["$skip"] = "" + (pageNumber * pageSize);
        queryParameters["$top"] = "" + pageSize;
    }

    if (odataParameters.filter != null) {
        queryParameters["$filter"] = odataParameters.filter;
    }

    if (odataParameters.orderby != null) {
        queryParameters["$orderby"] = odataParameters.orderby;
    }

    const parameters = Object.keys(queryParameters);

    return parameters.length == 0 ?
        null :
        parameters
            .map(parameter =>
                `${encodeURIComponent(parameter)}=${encodeURIComponent(queryParameters[parameter])}`
            )
            .join("&");
}

export {
    create,
    delete$,
    NoParameters,
    Pagination,
    retrieve,
    retrieveCollection,
    ODataParameters,
    update,
}
